import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { killCampaign, listRunningCampaigns } from '../../api/scriptsApi';
import { keys } from '../../lib/queryKeys';
import { scriptMonitorPath } from '../../lib/config';
import { formatTimeOnly } from '../../lib/format';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useToast } from '../../providers/ToastProvider';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Callout, Panel, PanelBody, PanelHeader, PanelSpacer, PanelTitle } from '../../components/ui/Panel';
import {
  CellActions,
  CellPrimary,
  CellSub,
  Table,
  TableEmpty,
  TableScroll,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '../../components/ui/Table';
import { canOfferKill, isActiveStatus, statusPresentation } from './scriptStatus';

const COLUMN_COUNT = 4;

/**
 * Running Bulk Scripts: every user's bulk campaigns that are running now (or ended in
 * the last half hour), with View Logs and Kill per campaign.
 *
 * Everything comes from backend-owned state, so a campaign shows here whether or not
 * its owner's browser is open. Each row is one campaign; the internal campaign id
 * travels with the row for the actions and is never rendered.
 */
export function RunningScriptsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Campaigns with a Kill request in flight. A ref for the synchronous double-click
  // guard, state so the buttons re-render disabled.
  const killingRef = useRef(new Set());
  const [killing, setKilling] = useState(() => new Set());

  const listQuery = useQuery({
    queryKey: keys.scripts.list,
    queryFn: ({ signal }) => listRunningCampaigns(signal),
    // Quick while anything is sending or stopping, relaxed when nothing is.
    refetchInterval: (query) => {
      const rows = query.state.data?.campaigns ?? [];
      return rows.some((row) => isActiveStatus(row.status)) ? 3_000 : 10_000;
    },
    placeholderData: (previous) => previous
  });

  const rows = listQuery.data?.campaigns ?? [];
  const isOperator = listQuery.data?.viewer?.isOperator === true;
  const hasData = Boolean(listQuery.data);

  function setRowStatus(campaignRef, status) {
    queryClient.setQueryData(keys.scripts.list, (old) =>
      old
        ? {
          ...old,
          campaigns: old.campaigns.map((row) => (row.campaignRef === campaignRef ? { ...row, status } : row))
        }
        : old
    );
  }

  function markKilling(campaignRef, on) {
    if (on) killingRef.current.add(campaignRef);
    else killingRef.current.delete(campaignRef);
    setKilling(new Set(killingRef.current));
  }

  async function handleKill(row) {
    if (killingRef.current.has(row.campaignRef)) return;

    const ok = await confirm({
      title: 'Kill this campaign?',
      message: [
        `User: ${row.ownerName}`,
        `Data file: ${row.dataFile}`
      ],
      confirmLabel: 'Kill campaign',
      cancelLabel: 'Keep running',
      tone: 'danger'
    });
    if (!ok || killingRef.current.has(row.campaignRef)) return;

    markKilling(row.campaignRef, true);
    try {
      const result = await killCampaign(row.campaignRef);
      // The server's confirmed status — 'stopping' until in-flight sends finish.
      if (result?.status) setRowStatus(row.campaignRef, result.status);
      toast.success(`✅ ${result?.message ?? 'Campaign stopped.'}`);
    } catch (err) {
      toast.error(`❌ ${err.message}`);
    } finally {
      markKilling(row.campaignRef, false);
      queryClient.invalidateQueries({ queryKey: keys.scripts.all });
    }
  }

  function killTitle(row) {
    if (!row.canKill) return "Only the campaign's owner or a campaign operator can stop it";
    if (row.status !== 'running') return 'Only a running campaign can be killed';
    return `Stop ${row.ownerName}'s campaign`;
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3">
      <header className="flex flex-wrap items-end gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-ink-900">Running Bulk Scripts</h1>
          <p className="text-sm text-muted">
            Bulk campaigns running across all users. Updates automatically.
          </p>
        </div>
      </header>

      {hasData && !isOperator ? (
        <Callout tone="info" icon="fa-circle-info">
          You can view every campaign and its logs, and stop your own. Stopping another user&apos;s campaign
          requires campaign operator access.
        </Callout>
      ) : null}

      {listQuery.isError && hasData ? (
        <Callout tone="warning" icon="fa-triangle-exclamation">
          Could not refresh ({listQuery.error.message}). Showing the last known state.
        </Callout>
      ) : null}

      <Panel>
        <PanelHeader>
          <PanelTitle icon="fa-terminal">Campaigns</PanelTitle>
          <PanelSpacer />
          {listQuery.data?.generatedAt ? (
            <span className="text-xs text-muted">Updated {formatTimeOnly(listQuery.data.generatedAt)}</span>
          ) : null}
          <Button
            size="xs"
            variant="outline"
            icon="fa-rotate"
            onClick={() => listQuery.refetch()}
            loading={listQuery.isFetching && !listQuery.isLoading}
            loadingLabel="Refreshing"
          >
            Refresh
          </Button>
        </PanelHeader>

        <PanelBody className="p-0">
          <TableScroll maxHeight="72vh" className="rounded-none border-0">
            <Table className="min-w-[560px]">
              <Thead>
                <tr>
                  <Th>User Name</Th>
                  <Th>Data File</Th>
                  <Th>Status</Th>
                  <Th align="center">Action</Th>
                </tr>
              </Thead>
              <Tbody>
                {listQuery.isLoading ? (
                  <TableEmpty colSpan={COLUMN_COUNT} loading title="Loading running campaigns…" />
                ) : listQuery.isError && !hasData ? (
                  <TableEmpty colSpan={COLUMN_COUNT} icon="fa-circle-exclamation" title="Could not load campaigns">
                    {listQuery.error.message}{' '}
                    <button type="button" className="font-semibold text-brand-600 underline" onClick={() => listQuery.refetch()}>
                      Try again
                    </button>
                  </TableEmpty>
                ) : rows.length === 0 ? (
                  <TableEmpty colSpan={COLUMN_COUNT} icon="fa-circle-pause" title="No bulk campaigns are running">
                    Campaigns started by any user appear here while they send.
                  </TableEmpty>
                ) : (
                  rows.map((row) => {
                    const status = statusPresentation(row.status);
                    const busy = killing.has(row.campaignRef);
                    return (
                      <Tr key={row.campaignRef}>
                        <Td className="align-middle">
                          <CellPrimary>{row.ownerName}</CellPrimary>
                        </Td>
                        <Td className="align-middle">
                          <CellPrimary className="break-all font-medium">{row.dataFile}</CellPrimary>
                          {row.status === 'stopped' && row.stoppedBy ? (
                            <CellSub>Stopped by {row.stoppedBy}</CellSub>
                          ) : null}
                        </Td>
                        <Td className="align-middle">
                          <Badge tone={status.tone} icon={status.icon}>
                            {status.label}
                          </Badge>
                        </Td>
                        <Td className="align-middle">
                          <CellActions className="flex-wrap">
                            <Button
                              size="sm"
                              variant="primary"
                              icon="fa-file-lines"
                              onClick={() => navigate(scriptMonitorPath(row.campaignRef))}
                            >
                              View Logs
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              icon="fa-skull-crossbones"
                              title={killTitle(row)}
                              disabled={!canOfferKill(row, killing)}
                              loading={busy}
                              loadingLabel="Killing…"
                              onClick={() => handleKill(row)}
                            >
                              Kill
                            </Button>
                          </CellActions>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableScroll>
        </PanelBody>
      </Panel>
    </div>
  );
}
